import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

export interface Ancla {
  id: string;
  etiqueta: string;
}

/**
 * Saltos rápidos a las secciones del tab Plan.
 *
 * La pestaña creció —planner, clima, objetivos, personal— hasta que lo de
 * abajo dejó de existir para quien la abre: nadie baja buscando algo que no
 * sabe que está. Esto lo pone todo a la vista en una fila y a un clic.
 *
 * **Se pega arriba al desplazar** (`sticky`): un índice que se va con el
 * scroll sólo sirve para el primer salto, y justo cuando estás abajo es cuando
 * más falta hace poder ir a otro sitio.
 *
 * La sección en la que estás se marca sola con un `IntersectionObserver`, que
 * es lo que convierte la fila en un mapa y no en cuatro botones sueltos. Sin
 * eso, después de saltar no hay forma de saber dónde te dejó.
 */
export function PlanAnchors({ anclas }: { anclas: Ancla[] }) {
  const t = useT();
  const [activa, setActiva] = useState<string | null>(null);
  /*
   * Cuándo fue el último clic, para que el observador no lo pise.
   *
   * La última sección de la página no puede llegar arriba del todo: no queda
   * scroll por debajo. El observador seguía viendo más superficie de la
   * anterior y devolvía el marcador a ella medio segundo después de pulsar —
   * pulsabas «Personal» y se quedaba marcado «Clima». Medido.
   *
   * Durante el desplazamiento suave manda el clic; después, el observador.
   */
  const ultimoClic = useRef(0);

  useEffect(() => {
    const secciones = anclas
      .map((a) => document.getElementById(a.id))
      .filter((el): el is HTMLElement => !!el);
    if (secciones.length === 0) return;
    const obs = new IntersectionObserver(
      (entradas) => {
        // La que más se vea manda. Con «la primera que entra» el marcador
        // parpadeaba entre dos secciones al pasar de una a otra.
        if (Date.now() - ultimoClic.current < 900) return;
        const visible = entradas
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiva(visible.target.id);
      },
      // El margen superior descuenta la propia fila pegada: sin él, la sección
      // se marca activa cuando todavía está debajo de la barra.
      { rootMargin: '-96px 0px -55% 0px', threshold: [0.1, 0.5, 1] },
    );
    secciones.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [anclas]);

  const ir = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    ultimoClic.current = Date.now();
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Se marca al pulsar y no se espera al observador: el desplazamiento suave
    // tarda, y medio segundo sin respuesta se lee como que el botón no va.
    setActiva(id);
  };

  return (
    <nav
      aria-label={t('plan.anchors.label')}
      className="sticky top-2 z-20 mb-4 flex flex-wrap items-center gap-1.5 rounded-xl bg-[var(--color-bg-elevated)] px-3 py-2 shadow-[var(--shadow-card)]"
    >
      <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
        {t('plan.anchors.label')}
      </span>
      {anclas.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => ir(a.id)}
          aria-current={activa === a.id ? 'true' : undefined}
          className={cn(
            'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
            activa === a.id
              ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
              : 'text-[var(--color-fg-muted)] hover:bg-[var(--color-bg-surface)] hover:text-[var(--color-fg-primary)]',
          )}
        >
          {a.etiqueta}
        </button>
      ))}
    </nav>
  );
}
